import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Folder, FolderTreeNode, Report, Dashboard, Chart, Form, TabGroup } from '@bi-studio/shared/lib';
import { getFolders, getRootItems, getFolder } from '@bi-studio/shared/apis';
import { RootState } from '../index';

interface FolderState {
    topLevelFolders: FolderTreeNode[];
    rootReports: Report[];
    rootDashboards: Dashboard[];
    rootCharts: Chart[];
    rootForms: Form[];
    rootTabGroups: TabGroup[];
    allFolders: FolderTreeNode[];

    currentFolder: (Folder & {
        reports: Report[];
        dashboards: Dashboard[];
        charts: Chart[];
        tabGroups: TabGroup[];
    }) | null;
    childFolders: FolderTreeNode[];

    isRootLoading: boolean;
    isFolderLoading: boolean;
    isRootLoaded: boolean;
    error: string | null;
}

const initialState: FolderState = {
    topLevelFolders: [],
    rootReports: [],
    rootDashboards: [],
    rootCharts: [],
    rootForms: [],
    rootTabGroups: [],
    allFolders: [],
    currentFolder: null,
    childFolders: [],
    isRootLoading: false,
    isFolderLoading: false,
    isRootLoaded: false,
    error: null,
};

export const fetchRootContent = createAsyncThunk(
    'folder/fetchRootContent',
    async (token: string, { rejectWithValue }) => {
        try {
            // Get all folders and filter for top-level (parentFolderId: null)
            const allFolders = await getFolders(token);
            const topLevelFolders = allFolders.filter((folder) => !folder.parentFolderId);

            // Get root-level items (items not in any folder)
            const rootItems = await getRootItems(token);

            return {
                topLevelFolders,
                rootReports: rootItems.reports,
                rootDashboards: rootItems.dashboards,
                rootCharts: rootItems.charts,
                rootForms: rootItems.forms,
                rootTabGroups: rootItems.tabGroups,
                allFolders: allFolders,
            };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch root content');
        }
    }
);

export const fetchFolderContent = createAsyncThunk(
    'folder/fetchFolderContent',
    async ({ token, folderId }: { token: string; folderId: string }, { rejectWithValue }) => {
        try {
            // Load folder details with items
            const folderData = await getFolder(token, folderId);

            // Load all folders to find children
            const allFolders = await getFolders(token);

            // Find child folders recursively
            const findChildFolders = (
                folders: FolderTreeNode[],
                parentId: string
            ): FolderTreeNode[] => {
                const children: FolderTreeNode[] = [];
                for (const folder of folders) {
                    if (folder.parentFolderId === parentId) {
                        children.push(folder);
                    }
                    if (folder.children && folder.children.length > 0) {
                        children.push(...findChildFolders(folder.children, parentId));
                    }
                }
                return children;
            };

            const children = findChildFolders(allFolders, folderId);

            return {
                currentFolder: folderData,
                childFolders: children,
            };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch folder data');
        }
    }
);

export const folderSlice = createSlice({
    name: 'folder',
    initialState,
    reducers: {
        setFolderError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        resetFolderState: (_state) => {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRootContent.pending, (state) => {
                state.isRootLoading = true;
                state.error = null;
            })
            .addCase(fetchRootContent.fulfilled, (state, action) => {
                state.isRootLoading = false;
                state.isRootLoaded = true;
                state.topLevelFolders = action.payload.topLevelFolders;
                state.rootReports = action.payload.rootReports;
                state.rootDashboards = action.payload.rootDashboards;
                state.rootCharts = action.payload.rootCharts;
                state.rootForms = action.payload.rootForms;
                state.rootTabGroups = action.payload.rootTabGroups;
                state.allFolders = action.payload.allFolders;
            })
            .addCase(fetchRootContent.rejected, (state, action) => {
                state.isRootLoading = false;
                state.error = action.payload as string;
            })
            // fetchFolderContent
            .addCase(fetchFolderContent.pending, (state) => {
                state.isFolderLoading = true;
                state.error = null;
                state.currentFolder = null;
                state.childFolders = [];
            })
            .addCase(fetchFolderContent.fulfilled, (state, action) => {
                state.isFolderLoading = false;
                state.currentFolder = action.payload.currentFolder;
                state.childFolders = action.payload.childFolders;
            })
            .addCase(fetchFolderContent.rejected, (state, action) => {
                state.isFolderLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setFolderError, resetFolderState } = folderSlice.actions;

// Selectors
export const selectTopLevelFolders = (state: RootState) => state.folder.topLevelFolders;
export const selectRootReports = (state: RootState) => state.folder.rootReports;
export const selectRootDashboards = (state: RootState) => state.folder.rootDashboards;
export const selectRootCharts = (state: RootState) => state.folder.rootCharts;
export const selectRootForms = (state: RootState) => state.folder.rootForms;
export const selectRootTabGroups = (state: RootState) => state.folder.rootTabGroups;
export const selectRootLoading = (state: RootState) => state.folder.isRootLoading;
export const selectRootLoaded = (state: RootState) => state.folder.isRootLoaded;
export const selectFolderLoading = (state: RootState) => state.folder.isFolderLoading;
export const selectFolderError = (state: RootState) => state.folder.error;
export const selectCurrentFolder = (state: RootState) => state.folder.currentFolder;
export const selectChildFolders = (state: RootState) => state.folder.childFolders;
export const selectAllFolders = (state: RootState) => state.folder.allFolders;

export default folderSlice.reducer;
